import { Modal, Table, Tag, Typography, Spin, Empty, Tabs } from "antd";
import { Calendar, Package, Truck, History as HistoryIcon, CalendarClock } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useGetHistoryServiceTicketsByInventoryId, useGetUpcomingServiceTicketsByInventoryId } from "~/services/service-ticket";
import { capitalize, formatDate } from "~/utils";
import { JobStatus, ServiceType } from "~/utils/constants";
import { TInventory } from "~/types/inventory";

const { Text } = Typography;

interface InventoryHistoryModalProps {
  inventory: TInventory | null;
  open: boolean;
  onClose: () => void;
}

const InventoryHistoryModal: React.FC<InventoryHistoryModalProps> = ({
  inventory,
  open,
  onClose,
}) => {
  const navigate = useNavigate();

  const { data: historyData, isLoading: isLoadingHistory } =
    useGetHistoryServiceTicketsByInventoryId(
      open ? inventory?.id : undefined
    );

  const { data: upcomingData, isLoading: isLoadingUpcoming } =
    useGetUpcomingServiceTicketsByInventoryId(
      open ? inventory?.id : undefined
    );

  const handleRowClick = (record: any) => {
    const bookingId = record.jobTicket?.id;
    if (bookingId) {
      navigate(`/bookings/${bookingId}?tab=services&serviceId=${record.id}`);
    }
  };

  const getServiceTypeIcon = (type: ServiceType) => {
    switch (type) {
      case ServiceType.DELIVERY:
        return <Truck size={14} />;
      case ServiceType.COLLECTION:
        return <Package size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return "green";
      case JobStatus.CANCELLED:
        return "red";
      case JobStatus.DRAFT:
        return "orange";
      case JobStatus.SCHEDULED:
        return "blue";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (text: ServiceType) => (
        <div className="flex items-center gap-1">
          {getServiceTypeIcon(text)}
          <span>{capitalize(text)}</span>
        </div>
      ),
    },
    {
      title: "Ticket #",
      dataIndex: "serial",
      key: "serial",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: (client: any) => client?.companyName || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: JobStatus) => (
        <Tag color={getStatusColor(status)}>{capitalize(status)}</Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "serviceDatetime",
      key: "serviceDatetime",
      render: (date: string) => formatDate(date),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location: any) => {
        if (!location) return "N/A";
        const city = location.city || "";
        const state = location.state || "";
        if (city && state) return `${city}, ${state}`;
        if (city) return city;
        if (state) return state;
        return "N/A";
      },
    },
  ];

  const historyTable = (
    <>
      {isLoadingHistory ? (
        <div className="flex justify-center items-center p-8">
          <Spin />
        </div>
      ) : !historyData || historyData.items.length === 0 ? (
        <Empty
          description="No previous service tickets found for this item"
          className="py-8"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Table
          dataSource={historyData.items}
          columns={columns}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" },
          })}
          pagination={
            historyData.items.length > 10
              ? {
                  total: historyData.meta.totalItems,
                  current: historyData.meta.currentPage,
                  pageSize: historyData.meta.itemsPerPage,
                }
              : false
          }
          size="small"
        />
      )}
    </>
  );

  const upcomingTable = (
    <>
      {isLoadingUpcoming ? (
        <div className="flex justify-center items-center p-8">
          <Spin />
        </div>
      ) : !upcomingData || upcomingData.items.length === 0 ? (
        <Empty
          description="No upcoming service tickets found for this item"
          className="py-8"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Table
          dataSource={upcomingData.items}
          columns={columns}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" },
          })}
          pagination={
            upcomingData.items.length > 10
              ? {
                  total: upcomingData.meta.totalItems,
                  current: upcomingData.meta.currentPage,
                  pageSize: upcomingData.meta.itemsPerPage,
                }
              : false
          }
          size="small"
        />
      )}
    </>
  );

  return (
    <Modal
      title={
        <div>
          <div className="text-lg font-medium">Service Tickets</div>
          {inventory && (
            <div className="text-sm text-gray-500 font-normal mt-1">
              {inventory.title} - {inventory.inventoryRef}
            </div>
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <Tabs
        defaultActiveKey="history"
        items={[
          {
            key: "history",
            label: (
              <span className="flex items-center gap-2">
                <HistoryIcon size={14} />
                History
                <span className="text-gray-500">
                  ({historyData?.items?.length || 0})
                </span>
              </span>
            ),
            children: historyTable,
          },
          {
            key: "upcoming",
            label: (
              <span className="flex items-center gap-2">
                <CalendarClock size={14} />
                Upcoming
                <span className="text-gray-500">
                  ({upcomingData?.items?.length || 0})
                </span>
              </span>
            ),
            children: upcomingTable,
          },
        ]}
      />
    </Modal>
  );
};

export default InventoryHistoryModal;
